import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import AtelierModal from './AtelierModal';

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [req, setReq] = useState(null);
  const resolver = useRef(null);
  const triggerRef = useRef(null);

  const confirm = useCallback((opts) => new Promise((resolve) => {
    triggerRef.current = typeof document !== 'undefined' ? document.activeElement : null;
    resolver.current = resolve;
    setReq({ cancelLabel: 'Cancel', danger: false, ...opts });
  }), []);

  const settle = useCallback((answer) => {
    const r = resolver.current;
    resolver.current = null;
    setReq(null);
    if (r) r(answer);
    const t = triggerRef.current;
    triggerRef.current = null;
    if (t && typeof t.focus === 'function') {
      setTimeout(() => { try { t.focus(); } catch (_) {} }, 0);
    }
  }, []);

  const confirmBtnRef = useRef(null);
  useEffect(() => {
    if (req && confirmBtnRef.current) confirmBtnRef.current.focus();
  }, [req]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {/* zIndex 80: above every drawer and sheet (SlideOver 61,
          BottomSheet 60). A confirm asked from inside one (the client
          page's doors, 2026-10-28) used to open behind it, unanswerable. */}
      {req && (
        <AtelierModal
          open
          onClose={() => settle(false)}
          showClose={false}
          bodyClassName="p-6"
          zIndex={80}
        >
          {req.eyebrow && (
            <div className="av-eyebrow text-accent mb-2">{req.eyebrow}</div>
          )}
          {req.title && (
            <h2 className="font-display text-ink leading-tight mb-2" style={{ fontSize: '1.5rem', fontWeight: 500 }}>
              {req.title}
            </h2>
          )}
          {req.body && (
            <p className="text-sm text-ink-2 leading-relaxed mb-6">{req.body}</p>
          )}
          <div className="flex flex-wrap justify-end gap-2.5">
            <button
              type="button"
              onClick={() => settle(false)}
              className="px-4 py-2.5 rounded-btn border border-line text-sm font-medium text-ink bg-paper-elev hover:bg-paper-deep transition-colors"
            >
              {req.cancelLabel}
            </button>
            {req.altLabel && (
              <button
                type="button"
                onClick={() => settle('alt')}
                className="px-4 py-2.5 rounded-btn border border-line text-sm font-medium text-ink bg-paper-elev hover:bg-paper-deep transition-colors"
              >
                {req.altLabel}
              </button>
            )}
            <button
              ref={confirmBtnRef}
              type="button"
              onClick={() => settle(true)}
              className="px-4 py-2.5 rounded-btn text-sm font-medium text-paper-elev transition-colors"
              style={{
                background: req.danger ? '#A54641' : 'var(--accent)',
              }}
            >
              {req.confirmLabel || 'Confirm'}
            </button>
          </div>
        </AtelierModal>
      )}
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    return (opts) => Promise.resolve(window.confirm(opts?.body || opts?.title || 'Are you sure?'));
  }
  return ctx;
};

export default ConfirmProvider;
